import React from 'react'
import { MdEdit } from 'react-icons/md'
import '@/styles/components/subscription-product-card.css'

const PRODUCT_IMAGE = '/milk-bottle.jpg'

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

/**
 * One line about a product's order.
 *
 *   standing  what the customer currently gets (blue)
 *   change    a quantity change or pause booked ahead (amber)
 *   ending    a delivery stopping (red)
 *
 * The colour is carried by a dot, not the text, so a card with three of them
 * does not turn into three coloured blocks.
 */
export type ProductAnnotationKind = 'standing' | 'change' | 'ending'

export interface ProductAnnotation {
  kind: ProductAnnotationKind
  text: string
}

interface Props {
  name: string
  /** Woo packshot; falls back to the bottle placeholder. */
  image?: string | null
  /** Per-unit price. Hidden when absent rather than shown as £0.00. */
  unitPrice?: number | null
  annotations?: ProductAnnotation[]
  /** Delivery cadence tag, e.g. "Weekly" or "Every 2 weeks". Hidden when absent. */
  frequency?: string | null
  /** The edit control. A bool so callers that only display can omit it. */
  showEdit?: boolean
  onEdit?: () => void
  /** Opening the product's detail, from the packshot or the name. */
  onViewDetail?: () => void
}

/**
 * A product in the customer's subscription, present or upcoming.
 *
 * Image on the left; a text column with the name and, beneath it, the
 * annotations about its order; price and the edit button down the right.
 * Built as its own card rather than borrowing the weekly panel's grid row,
 * which is shaped for per-day quantity columns.
 */
const SubscriptionProductCard: React.FC<Props> = ({
  name,
  image,
  unitPrice,
  annotations = [],
  frequency,
  showEdit = false,
  onEdit,
  onViewDetail,
}) => {
  const detailProps = onViewDetail
    ? { type: 'button' as const, onClick: onViewDetail }
    : { type: 'button' as const, disabled: true }

  return (
    <div className="subscription-card">
      <button
        className="subscription-card-thumb"
        aria-label={onViewDetail ? `About ${name}` : undefined}
        {...detailProps}
      >
        <img
          className="subscription-card-image"
          src={image || PRODUCT_IMAGE}
          alt=""
          onError={e => { e.currentTarget.src = PRODUCT_IMAGE }}
        />
      </button>

      <div className="subscription-card-text">
        <div className="subscription-card-heading">
          <button className="subscription-card-name" {...detailProps}>
            {name}
          </button>
          {frequency && (
            <span className="subscription-card-frequency">{frequency}</span>
          )}
        </div>

        {annotations.length > 0 && (
          <ul className="subscription-card-notes">
            {annotations.map((note, i) => (
              <li key={`${note.kind}-${i}`} className={`subscription-card-note is-${note.kind}`}>
                <span className="subscription-card-note-dot" aria-hidden="true" />
                <span>{note.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="subscription-card-side">
        {unitPrice != null && (
          <span className="subscription-card-price">
            {formatPrice(unitPrice)} each
          </span>
        )}

        {showEdit && (
          <button
            type="button"
            className="subscription-card-edit"
            onClick={onEdit}
            aria-label={`Change ${name}`}
          >
            <MdEdit aria-hidden="true" />
            Edit
          </button>
        )}
      </div>
    </div>
  )
}

export default SubscriptionProductCard
